
import { useEffect, useState } from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Users, FileText, Calendar, Clock, MapPin } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface TrainingSession {
  id: string;
  title: string;
  description: string;
  instructor: string;
  location: string;
  start_date: string;
  end_date: string;
  status: string;
}

interface Participant {
  user_id: string;
  full_name: string;
  email: string;
  was_present: boolean;
}

interface AttendanceRecord {
  id?: string;
  session_id: string;
  user_id: string;
  was_present: boolean;
  marked_by: string;
  marked_at: string;
}

const SessionParticipants = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useSupabaseUser();
  const { isAdmin, isLoading: isLoadingRoles } = useUserRoles();
  const [session, setSession] = useState<TrainingSession | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isLoadingRoles && user && isAdmin && sessionId) {
      fetchSessionData();
    }
  }, [user, isAdmin, isLoadingRoles, sessionId]);

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      
      // Fetch session details
      const { data: sessionData, error: sessionError } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;
      setSession(sessionData);

      // Fetch session participants with their profiles
      const { data: participantsData, error: participantsError } = await supabase
        .from('training_session_participants')
        .select('user_id')
        .eq('session_id', sessionId);

      if (participantsError) {
        console.error('Error fetching participants:', participantsError);
        throw participantsError;
      }

      // Fetch profiles for the participants
      const userIds = (participantsData as any)?.map((p: any) => p.user_id) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      // Fetch existing attendance records
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('training_attendance' as any)
        .select('user_id, was_present')
        .eq('session_id', sessionId);

      if (attendanceError && attendanceError.code !== 'PGRST116') {
        throw attendanceError;
      }

      // Create attendance map
      const attendanceMap = new Map();
      (attendanceData as any)?.forEach((record: any) => {
        attendanceMap.set(record.user_id, record.was_present);
      });

      // Combine participant data with attendance status and profiles
      const participantsWithAttendance = userIds.map((userId: string) => {
        const profile = (profilesData as any)?.find((p: any) => p.id === userId);
        return {
          user_id: userId,
          full_name: profile?.full_name || 'Unknown',
          email: profile?.email || '',
          was_present: attendanceMap.get(userId) || false
        };
      });

      setParticipants(participantsWithAttendance);

    } catch (error) {
      console.error('Error fetching session data:', error);
      toast.error(language === 'en' ? 'Failed to load session data' : 'Αποτυχία φόρτωσης δεδομένων συνεδρίας');
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = (userId: string, wasPresent: boolean) => {
    setParticipants(prev => 
      prev.map(participant => 
        participant.user_id === userId 
          ? { ...participant, was_present: wasPresent }
          : participant
      )
    );
  };

  const handleMarkAllPresent = () => {
    setParticipants(prev => 
      prev.map(participant => ({ ...participant, was_present: true }))
    );
  };

  const handleMarkAllAbsent = () => {
    setParticipants(prev => 
      prev.map(participant => ({ ...participant, was_present: false }))
    );
  };

  const handleSaveAttendance = async () => {
    try {
      setSaving(true);

      // Prepare attendance records
      const attendanceRecords = participants.map(participant => ({
        session_id: sessionId,
        user_id: participant.user_id,
        was_present: participant.was_present,
        marked_by: user?.id,
        marked_at: new Date().toISOString()
      }));

      // Upsert attendance records
      const { error } = await supabase
        .from('training_attendance' as any)
        .upsert(attendanceRecords, {
          onConflict: 'session_id,user_id'
        });

      if (error) throw error;

      toast.success(
        language === 'en' 
          ? 'Attendance saved successfully' 
          : 'Η παρουσία αποθηκεύτηκε με επιτυχία'
      );

    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(
        language === 'en' 
          ? 'Failed to save attendance' 
          : 'Αποτυχία αποθήκευσης παρουσίας'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateReport = () => {
    // Navigate to report generation page
    navigate(`/training/sessions/${sessionId}/report`);
  };

  if (loading || isLoadingRoles) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {language === 'en' ? 'Access Denied' : 'Απαγόρευση Πρόσβασης'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'en' 
              ? 'You do not have permission to access this page.' 
              : 'Δεν έχετε άδεια πρόσβασης σε αυτή τη σελίδα.'
            }
          </p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-4">
            {language === 'en' ? 'Session Not Found' : 'Η Συνεδρία Δεν Βρέθηκε'}
          </h1>
          <Button onClick={() => navigate('/training/sessions')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Back to Sessions' : 'Πίσω στις Συνεδρίες'}
          </Button>
        </div>
      </div>
    );
  }

  const presentCount = participants.filter(p => p.was_present).length;
  const absentCount = participants.length - presentCount;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {language === 'en' ? 'Session Participants' : 'Συμμετέχοντες Συνεδρίας'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'en' ? 'Manage attendance for training session' : 'Διαχείριση παρουσίας για εκπαιδευτική συνεδρία'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/training/sessions')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Back' : 'Πίσω'}
          </Button>
        </div>
      </div>

      {/* Session Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            {session.title}
          </CardTitle>
          <CardDescription>{session.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
              <span>{format(fromLocalISOString(session.start_date), 'MMM dd, yyyy HH:mm')}</span>
            </div>
            <div className="flex items-center">
              <Users className="h-4 w-4 mr-2 text-muted-foreground" />
              <span>{session.instructor}</span>
            </div>
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
              <span>{session.location}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attendance Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-green-600">{presentCount}</div>
            <p className="text-sm text-muted-foreground">
              {language === 'en' ? 'Present' : 'Παρόντες'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-red-600">{absentCount}</div>
            <p className="text-sm text-muted-foreground">
              {language === 'en' ? 'Absent' : 'Απόντες'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold">{participants.length}</div>
            <p className="text-sm text-muted-foreground">
              {language === 'en' ? 'Total Participants' : 'Σύνολο Συμμετεχόντων'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Participants List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            {language === 'en' ? 'Attendance List' : 'Λίστα Παρουσίας'}
          </CardTitle>
          <CardDescription>
            {language === 'en' 
              ? 'Mark who was present and who was absent' 
              : 'Σημειώστε ποιοι ήταν παρόντες και ποιοι απόντες'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {participants.length > 0 && (
            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllPresent}
                className="text-green-600 border-green-600 hover:bg-green-50"
              >
                {language === 'en' ? 'Mark All Present' : 'Όλοι Παρόντες'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAbsent}
                className="text-red-600 border-red-600 hover:bg-red-50"
              >
                {language === 'en' ? 'Mark All Absent' : 'Όλοι Απόντες'}
              </Button>
            </div>
          )}
          <div className="space-y-4">
            {participants.map((participant) => (
              <div key={participant.user_id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">{participant.full_name}</h3>
                  <p className="text-sm text-muted-foreground">{participant.email}</p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`present-${participant.user_id}`}
                      checked={participant.was_present}
                      onCheckedChange={(checked) => 
                        handleAttendanceChange(participant.user_id, !!checked)
                      }
                    />
                    <label 
                      htmlFor={`present-${participant.user_id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {language === 'en' ? 'Present' : 'Παρών'}
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`absent-${participant.user_id}`}
                      checked={!participant.was_present}
                      onCheckedChange={(checked) => 
                        handleAttendanceChange(participant.user_id, !checked)
                      }
                    />
                    <label 
                      htmlFor={`absent-${participant.user_id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {language === 'en' ? 'Absent' : 'Απών'}
                    </label>
                  </div>
                </div>
              </div>
            ))}

            {participants.length === 0 && (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">
                  {language === 'en' ? 'No participants found' : 'Δεν βρέθηκαν συμμετέχοντες'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'en' 
                    ? 'This session has no assigned participants.' 
                    : 'Αυτή η συνεδρία δεν έχει ανατεθεί σε συμμετέχοντες.'
                  }
                </p>
              </div>
            )}
          </div>

          {participants.length > 0 && (
            <div className="flex justify-between mt-6">
              <Button
                onClick={handleGenerateReport}
                variant="outline"
                disabled={saving}
              >
                <FileText className="h-4 w-4 mr-2" />
                {language === 'en' ? 'Generate Report' : 'Δημιουργία Αναφοράς'}
              </Button>
              
              <Button
                onClick={handleSaveAttendance}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    {language === 'en' ? 'Saving...' : 'Αποθήκευση...'}
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 mr-2" />
                    {language === 'en' ? 'Save Attendance' : 'Αποθήκευση Παρουσίας'}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionParticipants;
